import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { MobileNav } from '../components/MobileNav';
import { Sparkles, BookOpen, Calendar, ArrowRight, Tag, Loader2 } from 'lucide-react';
import { Footer } from '../components/Footer';
import SEO, { pageSEO } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, [selectedCategory]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const url = selectedCategory 
        ? `${API}/blog/posts?category=${encodeURIComponent(selectedCategory)}`
        : `${API}/blog/posts`;
      const response = await axios.get(url);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/blog/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen swirl-bg">
      <SEO {...pageSEO.blog} />
      <MobileNav />

      <main className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 via-amber-400 to-pink-500 mb-6 glow-purple">
            <BookOpen className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-mystical text-4xl md:text-5xl lg:text-6xl text-[#2D1B3D] font-bold mb-4">
            Mystic Insights
          </h1>
          <p className="text-[#4A3A5A] text-lg md:text-xl max-w-2xl mx-auto">
            Discover the wisdom of numerology, spiritual guidance, and the secrets to finding your destined love connection
          </p>
        </motion.div>

        {/* Category Filter */}
        {categories.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex flex-wrap justify-center gap-3 mb-12"
          >
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-5 py-2 rounded-full font-semibold transition-all ${
                !selectedCategory 
                  ? 'bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white shadow-lg' 
                  : 'bg-white/70 text-[#5B21B6] hover:bg-white'
              }`}
              data-testid="category-all"
            >
              All Posts
            </button>
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-5 py-2 rounded-full font-semibold transition-all ${
                  selectedCategory === category 
                    ? 'bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white shadow-lg' 
                    : 'bg-white/70 text-[#5B21B6] hover:bg-white'
                }`}
                data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {category}
              </button>
            ))}
          </motion.div>
        )}

        {/* Blog Posts */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-10 h-10 text-[#8B5CF6] animate-spin" />
          </div>
        ) : posts.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <BookOpen className="w-16 h-16 text-[#8B5CF6]/50 mx-auto mb-4" />
            <h3 className="font-mystical text-2xl text-[#2D1B3D] mb-2">No posts yet</h3>
            <p className="text-[#6D5A7D]">Check back soon for mystical insights!</p>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="ethereal-card rounded-3xl overflow-hidden group hover:shadow-2xl transition-all duration-300"
                data-testid={`blog-post-${post.slug}`}
              >
                {/* Image */}
                {post.image_url ? (
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={post.image_url} 
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                ) : (
                  <div className="h-48 bg-gradient-to-br from-[#8B5CF6]/20 via-[#D4AF37]/20 to-[#EC4899]/20 flex items-center justify-center">
                    <Sparkles className="w-16 h-16 text-[#8B5CF6]/40" />
                  </div>
                )}

                {/* Content */}
                <div className="p-6">
                  {/* Category & Date */}
                  <div className="flex items-center gap-4 mb-3 text-sm">
                    <span className="inline-flex items-center gap-1 text-[#8B5CF6] font-semibold">
                      <Tag className="w-4 h-4" />
                      {post.category}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[#8C7A9C]">
                      <Calendar className="w-4 h-4" />
                      {formatDate(post.created_at)}
                    </span>
                  </div>

                  {/* Title */}
                  <h2 className="font-mystical text-2xl text-[#2D1B3D] font-bold mb-3 group-hover:text-[#8B5CF6] transition-colors">
                    {post.title}
                  </h2>

                  {/* Excerpt */}
                  <p className="text-[#5D4A6D] mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>

                  {/* Read More */}
                  <Link 
                    to={`/blog/${post.slug}`}
                    className="inline-flex items-center gap-2 text-[#8B5CF6] font-bold hover:text-[#D4AF37] transition-colors"
                    data-testid={`read-more-${post.slug}`}
                  >
                    Read More
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        )}

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-16 text-center"
        >
          <div className="ethereal-card rounded-3xl p-10 inline-block">
            <h3 className="font-mystical text-2xl text-[#2D1B3D] font-bold mb-3">
              Want a Personal Reading?
            </h3>
            <p className="text-[#5D4A6D] mb-6">
              Discover what the numbers reveal about your love life and destiny
            </p>
            <Link to="/contact" data-testid="blog-cta-contact">
              <Button className="rounded-full btn-magic px-8 py-4 font-bold">
                Book a Session with Karine
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </motion.div>

        {/* Savvy Singles Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-16"
        >
          <div className="ethereal-card rounded-3xl p-8 md:p-10">
            <div className="text-center mb-8">
              <h3 className="font-mystical text-2xl md:text-3xl text-[#2D1B3D] font-bold mb-3">
                More Dating & Relationship Insights
              </h3>
              <p className="text-[#5D4A6D]">
                Check out the Savvy Singles blog for more expert dating advice
              </p>
            </div>
            
            <div className="flex flex-col items-center gap-6">
              <a 
                href="https://www.savvysingles.com/blog" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-xl hover:scale-105 transition-all"
                data-testid="savvy-singles-blog-link"
              >
                <BookOpen className="w-5 h-5" />
                Visit Savvy Singles Blog
                <ArrowRight className="w-5 h-5" />
              </a>
              
              <div className="flex items-center gap-2 text-[#5D4A6D]">
                <span className="text-sm">A+ BBB Rated</span>
                <span className="text-[#D4AF37]">★★★★★</span>
              </div>
            </div>
          </div>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPage;
